<template>
    <div class="af-work-preferences">
        <PageHeading>{{ $t('work_preferences.work_preferences') }}</PageHeading>

        <Submenu
            :menu="submenu"
            @menuChange="changeTab"
        />

        <div v-show="isNextRoleActive">
            <template v-if="!$_seekerFieldByCode('desired_occupations').is_hidden">
                <SeekerOccupations />
            </template>
            <template v-if="!$_seekerFieldByCode('job_type').is_hidden">
                <SeekerAttributesGroup
                    :attribute-id="1"
                    :options="jobType"
                    :label="$_seekerFieldByCode('job_type').label"
                    :disabled="$_seekerFieldByCode('job_type').read_only"
                />
            </template>
            <template v-if="!$_seekerFieldByCode('contract_type').is_hidden">
                <SeekerAttributesGroup
                    :attribute-id="2"
                    :options="contractType"
                    :label="$_seekerFieldByCode('contract_type').label"
                    :disabled="$_seekerFieldByCode('contract_type').read_only"
                />
            </template>
            <div class="afu-grid md:afu-grid-cols-2 md:afu-gap-8">
                <template v-if="!$_seekerFieldByCode('work_authorisation').is_hidden">
                    <C1FormGroup>
                        <C1Select
                            v-model="user.work_authorisation"
                            :label="$_seekerFieldByCode('work_authorisation').label || $t('label.work_authorisation')"
                            label-for="workAuthorisation"
                            :options="workAuthorisation"
                            :placeholder="$t('label.work_authorisation')"
                            label-key="value"
                            @input="updateWorkAuthorisation"
                            :disabled="$_seekerFieldByCode('work_authorisation').read_only"
                        />
                    </C1FormGroup>
                </template>
                <template v-if="!$_seekerFieldByCode('career_level').is_hidden">
                    <C1FormGroup>
                        <C1Select
                            v-model="user.career_level"
                            :label="$_seekerFieldByCode('career_level').label || $t('label.career_level')"
                            label-for="careerLevel"
                            :options="careerLevel"
                            :placeholder="$t('label.career_level')"
                            :disabled="$_seekerFieldByCode('career_level').read_only"
                            label-key="value"
                            @input="updateCareerLevel"
                        />
                    </C1FormGroup>
                </template>
            </div>

            <div class="afu-grid md:afu-grid-cols-2 md:afu-gap-8">
                <template v-if="!$_seekerFieldByCode('desired_pay').is_hidden">
                    <C1FormGroup>
                        <div class="afu-flex afu-items-center afu-justify-between">
                            <C1Label>
                                {{ $_seekerFieldByCode('desired_pay').label || $t('label.desired_pay') }}
                            </C1Label>

                            <PayBandMenu
                                v-model="payType"
                                :disabled="$_seekerFieldByCode('desired_pay').read_only"
                                @input="onPayTypeSelect"
                            />
                        </div>

                        <C1Select
                            v-if="payType === 'Annually'"
                            v-model="user.min_pay_band"
                            :options="payBandAnnuallyFiltered"
                            :disabled="$_seekerFieldByCode('desired_pay').read_only"
                            label-key="label"
                            value-key="id"
                            placeholder="Select range"
                            @input="updateMinPayBand"
                        />

                        <C1Select
                            v-if="payType === 'Hourly'"
                            v-model="user.min_pay_band"
                            :options="payBandHourlyFiltered"
                            :disabled="$_seekerFieldByCode('desired_pay').read_only"
                            label-key="label"
                            value-key="id"
                            placeholder="Select range"
                            @input="updateMinPayBand"
                        />
                    </C1FormGroup>
                </template>

                <template v-if="!$_seekerFieldByCode('availability').is_hidden">
                    <C1FormGroup>
                        <C1Select
                            id="noticePeriod"
                            v-model="user.notice_period"
                            :label="$_seekerFieldByCode('availability').label || $t('label.availability')"
                            label-for="noticePeriod"
                            :options="noticePeriod"
                            :placeholder="$t('label.availability')"
                            :disabled="$_seekerFieldByCode('availability').read_only"
                            label-key="value"
                            @input="updateNoticePeriod()"
                        />
                    </C1FormGroup>
                </template>
            </div>

            <template v-if="!$_seekerFieldByCode('desired_locations').is_hidden">
                <SeekerLocations />
            </template>
        </div>

        <div v-show="isOtherPreferencesActive">
            <template v-if="!$_seekerFieldByCode('perks').is_hidden">
                <SeekerAttributesTypeahead
                    :attribute-id="21"
                    :label="$_seekerFieldByCode('perks').label"
                    :disabled="$_seekerFieldByCode('perks').read_only"
                    attribute-code="perks"
                    uppercase
                    allow-new
                />
            </template>

            <template v-if="!$_seekerFieldByCode('sector').is_hidden">
                <SeekerAttributesGroup
                    :attribute-id="3"
                    :options="sector"
                    :label="$_seekerFieldByCode('sector').label"
                    :disabled="$_seekerFieldByCode('sector').read_only"
                />
            </template>
            <template v-if="!$_seekerFieldByCode('industries').is_hidden">
                <SeekerAttributesDropdown
                    :attribute-id="4"
                    :options="industry"
                    :label="$_seekerFieldByCode('industries').label"
                    :disabled="$_seekerFieldByCode('industries').read_only"
                />
            </template>
            <template v-if="!$_seekerFieldByCode('job_mode').is_hidden">
                <SeekerAttributesDropdown
                    :attribute-id="5"
                    :options="jobMode"
                    :label="$_seekerFieldByCode('job_mode').label"
                    :disabled="$_seekerFieldByCode('job_mode').read_only"
                />
            </template>
            <template v-if="!$_seekerFieldByCode('company_size').is_hidden">
                <SeekerAttributesGroup
                    :attribute-id="8"
                    :options="companySize"
                    class="mb-0"
                    :label="$_seekerFieldByCode('company_size').label"
                    :disabled="$_seekerFieldByCode('company_size').read_only"
                />
            </template>

            <SeekerCustomAttributes :attribute-group="'work'" />
        </div>
    </div>
</template>

<script>
    import { mapState, mapActions, mapGetters } from 'vuex';
    import numeral from 'numeral';
    import has from 'lodash/has';
    import SeekerAttributesTypeahead from '@/apps/dashboard/001/components/SeekerAttributesTypeahead';
    import PageHeading from '@/apps/dashboard/001/components/PageHeading';
    import SeekerAttributesGroup from '@/apps/dashboard/001/components/SeekerAttributesGroup';
    import SeekerAttributesDropdown from '@/apps/dashboard/001/components/SeekerAttributesDropdown';
    import PayBandMenu from '@/apps/dashboard/001/components/PayBandMenu';
    import SeekerLocations from '@/apps/dashboard/001/components/SeekerLocations';
    import C1FormGroup from '@/components/C1Components/C1FormGroup';
    import Submenu from '@/apps/dashboard/001/components/Submenu';
    import C1Select from '@/components/C1Components/C1Select';
    import C1Label from '@/components/C1Components/C1Label';
    import SeekerOccupations from '@/apps/dashboard/001/components/SeekerOccupations';
    import SeekerCustomAttributes from '@/apps/dashboard/001/components/SeekerCustomAttributes';

    export default {
        name: 'WorkPreferences',

        components: {
            SeekerOccupations,
            C1Label,
            C1Select,
            Submenu,
            C1FormGroup,
            PageHeading,
            SeekerAttributesTypeahead,
            SeekerAttributesGroup,
            PayBandMenu,
            SeekerLocations,
            SeekerAttributesDropdown,
            SeekerCustomAttributes,
        },

        data() {
            return {
                submenu: [],
                user: {},
                payType: 'Annually',
                jobType: [],
                contractType: [],
                sector: [],
                companySize: [],
                careerLevel: [],
                workAuthorisation: [],
                noticePeriod: [],
                educationLevel: [],
                industry: [],
                jobMode: [],
                payBandAnnually: [],
                payBandHourly: [],
            };
        },

        computed: {
            ...mapState({
                $_me: state => state.me.user,
            }),

            ...mapGetters('site', {
                $_seekerFieldByCode: 'seekerFieldByCode',
                $_seekerSectionByCode: 'seekerSectionByCode',
            }),

            payBandAnnuallyFiltered() {
                const payBandAnnually = JSON.parse(
                    JSON.stringify(this.payBandAnnually),
                );
                payBandAnnually.forEach(o => {
                    if (o.value > 0) {
                        o.label = this.$t('global.currency_format').format(
                            numeral(o.value).format('0,0'),
                        );
                    }
                });

                return payBandAnnually;
            },

            payBandHourlyFiltered() {
                const payBandHourly = JSON.parse(
                    JSON.stringify(this.payBandHourly),
                );
                payBandHourly.forEach(o => {
                    if (o.value > 0) {
                        o.label = this.$t('global.currency_format').format(
                            numeral(o.value).format('0,0'),
                        );
                    }
                });

                return payBandHourly;
            },

            isNextRoleActive() {
                const menu = this.findMenuByLabel('Your next role');
                return menu ? menu.is_active : false;
            },

            isOtherPreferencesActive() {
                const menu = this.findMenuByLabel('Other preferences');
                return menu ? menu.is_active : false;
            },
        },

        async created() {
            let next_role = [];
            if (!this.$_seekerSectionByCode('next_role').is_hidden) {
                next_role = [
                    { id: 'next_role', label: 'Your next role', is_active: false },
                ];
            }

            let other_pref = [];
            if (!this.$_seekerSectionByCode('other_pref').is_hidden) {
                other_pref = [
                    {
                        id: 'other_pref',
                        label: 'Other preferences',
                        is_active: false,
                    },
                ];
            }

            this.submenu = [...next_role, ...other_pref];

            if (this.submenu[0]) {
                this.submenu[0].is_active = true;
            }

            this.getInitialData();

            if (
                has(this.$_me, 'minPayBand.pay_type_id') &&
                this.$_me.minPayBand.pay_type_id === 2
            ) {
                this.payType = 'Hourly';
            }

            this.user = {
                career_level: this.$_me.careerLevel || {},
                work_authorisation: this.$_me.workAuthorisation || {},
                education_level: this.$_me.educationLevel || {},
                notice_period: this.$_me.noticePeriod || {},
                min_pay_band: this.$_me.minPayBand || {},
            };
        },

        methods: {
            ...mapActions('me', {
                $_updateMyDetails: 'updateMyDetails',
                $_syncAttributes: 'syncAttributes',
                $_getMe: 'getMe',
            }),

            ...mapActions('attribute', {
                $_getJobType: 'getJobType',
                $_getContractType: 'getContractType',
                $_getSector: 'getSector',
                $_getCompanySize: 'getCompanySize',
                $_getCareerLevel: 'getCareerLevel',
                $_getWorkAuthorisation: 'getWorkAuthorisation',
                $_getEducationLevel: 'getEducationLevel',
                $_getNoticePeriod: 'getNoticePeriod',
                $_getIndustry: 'getIndustry',
                $_getJobMode: 'getJobMode',
            }),

            ...mapActions('payType', {
                $_getAnnualSalary: 'getAnnualSalary',
                $_getHourlyRate: 'getHourlyRate',
            }),

            getInitialData() {
                this.$_getJobType().then(response => {
                    this.jobType = response.data;
                });
                this.$_getContractType().then(response => {
                    this.contractType = response.data;
                });
                this.$_getSector().then(response => {
                    this.sector = response.data;
                });
                this.$_getCompanySize().then(response => {
                    this.companySize = response.data;
                });
                this.$_getCareerLevel().then(response => {
                    this.careerLevel = response.data;
                });
                this.$_getWorkAuthorisation().then(response => {
                    this.workAuthorisation = response.data;
                });
                this.$_getNoticePeriod().then(response => {
                    this.noticePeriod = response.data;
                });
                this.$_getEducationLevel().then(response => {
                    this.educationLevel = response.data;
                });
                this.$_getIndustry().then(response => {
                    this.industry = response.data;
                });
                this.$_getJobMode().then(response => {
                    this.jobMode = response.data;
                });
                this.$_getAnnualSalary().then(response => {
                    this.payBandAnnually = response.data;
                });
                this.$_getHourlyRate().then(response => {
                    this.payBandHourly = response.data;
                });
            },

            updateCareerLevel() {
                const data = {
                    career_level_opt_id: has(this.user, 'career_level.id')
                        ? this.user.career_level.id
                        : '',
                };

                this.updateProfile(data);
            },

            updateNoticePeriod() {
                const data = {
                    notice_period_opt_id: has(this.user, 'notice_period.id')
                        ? this.user.notice_period.id
                        : '',
                };

                this.updateProfile(data);
            },

            updateWorkAuthorisation() {
                const data = {
                    work_authorisation_opt_id: has(
                        this.user,
                        'work_authorisation.id',
                    )
                        ? this.user.work_authorisation.id
                        : '',
                };

                this.updateProfile(data);
            },

            updateMinPayBand() {
                const data = {
                    min_pay_band_id: has(this.user, 'min_pay_band.id')
                        ? this.user.min_pay_band.id
                        : '',
                };

                this.updateProfile(data);
            },

            updateProfile(data) {
                this.$_updateMyDetails(data);
            },

            onPayTypeSelect() {
                this.user.min_pay_band = {};
            },

            findMenuByLabel(label) {
                return this.submenu.find(o => o.label === label);
            },

            changeTab(nav) {
                this.submenu.forEach(o => (o.is_active = nav.label === o.label));
            },
        },
    };
</script>
